/**
 * Hook for managing CIW state with persistence
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  CIWSessionState,
  ConsoleMessage,
  loadCIWState,
  saveCIWState,
  addMessageToState,
  addCommandToHistory,
  updateCurrentCommand,
  updatePreferences,
  clearCIWState,
  getCommandHistory,
  searchMessages,
  exportMessages,
  getSessionInfo
} from '../utils/ciwStatePersistence'
import { logInfo, logError } from '../utils/errorLogger'

export interface CIWStateManager {
  // State
  state: CIWSessionState
  
  // Message management
  addMessage: (message: string, type?: ConsoleMessage['type']) => void
  clearMessages: () => void
  searchMessages: (searchTerm: string) => ConsoleMessage[]
  
  // Command management
  currentCommand: string
  setCurrentCommand: (command: string) => void
  submitCommand: (command: string) => void
  getCommandHistory: () => string[]
  
  // Session management
  clearSession: () => void
  exportSession: () => string
  getSessionInfo: () => ReturnType<typeof getSessionInfo>
  
  // Preferences
  updatePreferences: (preferences: Partial<CIWSessionState['preferences']>) => void
  
  // Utilities
  isLoading: boolean
  error: string | null
}

export function useCIWState(): CIWStateManager {
  const [state, setState] = useState<CIWSessionState>(() => loadCIWState())
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const mountedRef = useRef(true)

  // Load state on mount
  useEffect(() => {
    try {
      setIsLoading(true)
      const loadedState = loadCIWState()
      
      if (mountedRef.current) {
        setState(loadedState)
        // Reduced logging: logInfo('CIW state loaded successfully', 'CIW')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to load CIW state: ${errorMessage}`)
      logError(`Failed to load CIW state: ${errorMessage}`, 'CIW')
    } finally {
      if (mountedRef.current) {
        setIsLoading(false)
      }
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Auto-save state changes with debouncing to prevent infinite loops
  useEffect(() => {
    if (mountedRef.current && !isLoading) {
      const saveTimeout = setTimeout(() => {
        try {
          saveCIWState(state)
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error'
          logError(`Failed to save CIW state: ${errorMessage}`, 'CIW')
        }
      }, 500) // Debounce saves by 500ms

      return () => clearTimeout(saveTimeout)
    }
  }, [state, isLoading])

  /**
   * Add a message to the console
   */
  const addMessage = useCallback((message: string, type: ConsoleMessage['type'] = 'info') => {
    try {
      setState(prevState => addMessageToState(prevState, message, type))
      setError(null)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to add message: ${errorMessage}`)
      logError(`Failed to add message: ${errorMessage}`, 'CIW')
    }
  }, [])

  /**
   * Clear all messages
   */
  const clearMessages = useCallback(() => {
    try {
      setState(prevState => ({
        ...prevState,
        messages: [{
          id: Date.now().toString(),
          timestamp: new Date(),
          message: 'Console cleared',
          type: 'info'
        }],
        lastActivity: new Date()
      }))
      setError(null)
      logInfo('CIW messages cleared', 'CIW')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to clear messages: ${errorMessage}`)
      logError(`Failed to clear messages: ${errorMessage}`, 'CIW')
    }
  }, [])

  /**
   * Search messages
   */
  const searchMessagesCallback = useCallback((searchTerm: string): ConsoleMessage[] => {
    try {
      return searchMessages(state, searchTerm)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      logError(`Failed to search messages: ${errorMessage}`, 'CIW')
      return []
    }
  }, [state])

  /**
   * Set current command
   */
  const setCurrentCommand = useCallback((command: string) => {
    try {
      setState(prevState => updateCurrentCommand(prevState, command))
      setError(null)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to update current command: ${errorMessage}`)
      logError(`Failed to update current command: ${errorMessage}`, 'CIW')
    }
  }, [])

  /**
   * Submit a command
   */
  const submitCommand = useCallback((command: string) => {
    try {
      // Add command to console
      setState(prevState => {
        let newState = addMessageToState(prevState, `> ${command}`, 'command')
        newState = addCommandToHistory(newState, command)
        newState = updateCurrentCommand(newState, '') // Clear current command
        return newState
      })
      setError(null)
      // Reduced logging: logInfo(`Command submitted: ${command}`, 'CIW')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to submit command: ${errorMessage}`)
      logError(`Failed to submit command: ${errorMessage}`, 'CIW')
    }
  }, [])

  /**
   * Get command history
   */
  const getCommandHistoryCallback = useCallback((): string[] => {
    try {
      return getCommandHistory()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      logError(`Failed to get command history: ${errorMessage}`, 'CIW')
      return []
    }
  }, [])

  /**
   * Clear entire session
   */
  const clearSession = useCallback(() => {
    try {
      clearCIWState()
      const newState = loadCIWState()
      setState(newState)
      setError(null)
      logInfo('CIW session cleared', 'CIW')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to clear session: ${errorMessage}`)
      logError(`Failed to clear session: ${errorMessage}`, 'CIW')
    }
  }, [])

  /**
   * Export session as text
   */
  const exportSession = useCallback((): string => {
    try {
      return exportMessages(state)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      logError(`Failed to export session: ${errorMessage}`, 'CIW')
      return `Error exporting session: ${errorMessage}`
    }
  }, [state])

  /**
   * Get session info
   */
  const getSessionInfoCallback = useCallback(() => {
    try {
      return getSessionInfo(state)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      logError(`Failed to get session info: ${errorMessage}`, 'CIW')
      return {
        sessionDuration: 0,
        lastActivityAge: 0,
        messageCount: 0,
        commandCount: 0,
        isActive: false
      }
    }
  }, [state])

  /**
   * Update preferences
   */
  const updatePreferencesCallback = useCallback((preferences: Partial<CIWSessionState['preferences']>) => {
    try {
      setState(prevState => updatePreferences(prevState, preferences))
      setError(null)
      logInfo('CIW preferences updated', 'CIW')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to update preferences: ${errorMessage}`)
      logError(`Failed to update preferences: ${errorMessage}`, 'CIW')
    }
  }, [])

  return {
    // State
    state,
    
    // Message management
    addMessage,
    clearMessages,
    searchMessages: searchMessagesCallback,
    
    // Command management
    currentCommand: state.currentCommand,
    setCurrentCommand,
    submitCommand,
    getCommandHistory: getCommandHistoryCallback,
    
    // Session management
    clearSession,
    exportSession,
    getSessionInfo: getSessionInfoCallback,
    
    // Preferences
    updatePreferences: updatePreferencesCallback,
    
    // Utilities
    isLoading,
    error
  }
}
